/**
 * T2 — read-only assessment detail for admins & managers.
 *
 * Deliberately NOT the editor: there are no inputs, no Save/Edit, no AI
 * instructions, no team-assignment controls, no change history and no
 * delete/danger zone. It presents the *definition* of an assessment —
 * name + description as a header, then each measure as a titled card
 * listing its labels and the detection phrases that identify each
 * label, all as plain readable text.
 *
 * Data comes from the scoped endpoint (``getAssessmentReadOnly``) which
 * 404s when the assessment isn't assigned to a team in the actor's
 * scope, so this view never shows anything the role can't see.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  api,
  type AssessmentReadOnlyDetail,
  type MeasureOut,
} from "../lib/api";

type ReadableLabel = { name: string; phrases: string[] };

// Pull the multi_label config into a plain, render-friendly shape.
// Other measure kinds aren't shipped yet; if one appears we render its
// kind as a fallback heading with no labels rather than crashing.
function readMeasure(m: MeasureOut): { label: string; labels: ReadableLabel[] } {
  const cfg = (m.config || {}) as {
    label?: string;
    labels?: { name?: string; detection_phrases?: string[] }[];
  };
  return {
    label: cfg.label ?? m.kind,
    labels: (cfg.labels ?? []).map((l) => ({
      name: l.name ?? "",
      phrases: (l.detection_phrases ?? []).filter(Boolean),
    })),
  };
}

export function AssessmentReadOnlyView({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const detailQ = useQuery<AssessmentReadOnlyDetail>({
    queryKey: ["assessment", "read-only", id],
    queryFn: () => api.getAssessmentReadOnly(id),
    refetchInterval: false,
  });

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="hover-elevate inline-flex items-center gap-1.5 rounded px-2 py-1 -ml-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to assessments
      </button>

      {detailQ.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : detailQ.error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          <div className="font-medium">Couldn't load this assessment</div>
          <div className="text-sm mt-1">
            {(detailQ.error as { message?: string })?.message ||
              "It may not be assigned to one of your teams."}
          </div>
        </div>
      ) : detailQ.data ? (
        <ReadOnlyBody detail={detailQ.data} />
      ) : null}
    </div>
  );
}

function ReadOnlyBody({ detail }: { detail: AssessmentReadOnlyDetail }) {
  const measures = detail.measures.map(readMeasure);
  return (
    <div className="space-y-6">
      {/* Header: name + description */}
      <header className="space-y-1.5">
        <h2 className="text-xl font-semibold text-foreground">{detail.name}</h2>
        {detail.description ? (
          <p className="text-sm text-muted-foreground max-w-3xl whitespace-pre-line">
            {detail.description}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No description provided.
          </p>
        )}
      </header>

      {/* Measures */}
      {measures.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground shadow-md">
          This assessment doesn't define any measures yet.
        </div>
      ) : (
        <div className="space-y-4">
          {measures.map((m, mi) => (
            <section
              key={mi}
              className="rounded-lg border border-border bg-card shadow-md animate-card-enter"
            >
              <div className="border-b border-border px-4 py-3">
                <h3 className="text-base font-semibold text-foreground">
                  {m.label}
                </h3>
              </div>
              <div className="p-4">
                {m.labels.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No labels defined.
                  </p>
                ) : (
                  <ul className="space-y-4">
                    {m.labels.map((l, li) => (
                      <li key={li} className="space-y-1.5">
                        <div className="text-sm font-medium text-foreground">
                          {l.name}
                        </div>
                        {l.phrases.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            No detection phrases.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {l.phrases.map((p, pi) => (
                              <span
                                key={pi}
                                className="inline-flex rounded-full bg-muted border border-border px-2 py-0.5 text-xs text-foreground"
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
